<script setup lang="ts">
import { computed, defineComponent } from "vue";
import { Language, SentenceTranslationData, TranslationMode } from "@lingvo-news/contracts";
import TranslatedTextV2 from "../TranslatedTextV2.vue";
import {
  getArticleTitleClass,
} from "../../styles/get-text-classes";
import { formatUserLocalDate } from "../../../utils/format-user-local-date";

defineComponent({
  name: "NewsItemStyle2",
});

const props = defineProps({
  mode: {
    type: Object as () => TranslationMode,
    required: true,
  },
  transliterate: {
    type: Boolean,
    required: true,
  },
  soundEnabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  titleData: {
    type: Object as () => SentenceTranslationData,
    required: true,
  },
  imageUrl: {
    type: String,
    required: false,
    default: "",
  },
  date: {
    type: String,
    required: false,
    default: "",
  },
  source: {
    type: String,
    required: false,
    default: "",
  },
  sourceLang: {
    type: String,
    required: false,
    default: "",
  },
  targetLang: {
    type: String,
    required: false,
    default: "",
  },
  link: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['open', 'audio-auth-required', 'audio-paywall-required']);

const handleOpenClick = () => {
  emit('open', props.link);
};

const formattedDate = computed(() => formatUserLocalDate(props.date, ''));

</script>

<template>
  <button
      class="news-item-style-2"
      type="button"
      :aria-label="$t('common.open')"
      @click="handleOpenClick"
  >
    <img :src="imageUrl" class="banner" alt=""/>
    <div class="content-overlay">
      <div class="title">
        <span class="date">{{ formattedDate }}&nbsp;&nbsp;</span>
        <TranslatedTextV2
            :mode="mode"
            :translationData="titleData"
            :showButtons="true"
            :translate="translate"
            :transliterate="transliterate"
            :soundEnabled="soundEnabled"
            :primaryTextClass="getArticleTitleClass(sourceLang as Language)"
            :secondaryTextClass="getArticleTitleClass(targetLang as Language)"
            @audio-auth-required="emit('audio-auth-required')"
            @audio-paywall-required="emit('audio-paywall-required')"
        />
      </div>
    </div>
  </button>
</template>

<style scoped lang="less">
@import "../../styles/text-styles.less";

.news-item-style-2 {
  --button-padding-y-scale: 1;
  position: relative;
  display: block;
  width: 100%;
  height: 210px;
  border-radius: 13px;
  overflow: hidden;
  cursor: pointer;
  border: 0;
  padding: 0;
  background: #1a1a2e;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
  user-select: none;
  outline: none;

  &:hover .banner {
    transform: scale(1.03);
  }

  &:hover .content-overlay::before {
    opacity: 0.95;
  }
}

.banner {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.content-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 18px 20px 16px;
  z-index: 1;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: calc(100% - 10px);
    //background: #f8ffff;
    background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.7) 30%, #878787 90%);
    pointer-events: none;
    z-index: -1;
    transition: opacity 0.3s ease;
  }
}

.title {
  font-size: 20px;
  color: #ffffff;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6), 0 0 12px rgba(0, 0, 0, 0.4);

  :deep(.translated-text-v2) {
    color: #ffffff;
    line-height: var(--line-height-title);
  }

  :deep(.translated-word) {
    color: #c3f6ff;
  }

  :deep(.replacement-word) {
    color: #c3f6ff;
  }
}

.date {
  font-size: inherit;
  color: rgba(255, 255, 255, 0.5);
  position: absolute;
  margin-top: -20px;
  //text-shadow: black 0 0 4px;

}
//.date {
//  font-size: inherit;
//  color: rgba(255, 255, 255, 0.7);
//  display: inline;
//}

.title :deep(.translated-text-v2) {
  display: inline;
}

.title :deep(.text-content),
.title :deep(.full-translation-content) {
  display: inline;
}

@media (max-width: 768px) {
  .news-item-style-2 {
    height: 170px;
    border-radius: 12px;
  }

  .content-overlay {
    padding: 14px 16px 12px;
  }

  .title {
    font-size: 13px;
  }

  .date {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .news-item-style-2 {
    height: 140px;
    border-radius: 10px;
  }

  .content-overlay {
    padding: 10px 12px 10px;
  }

  .title {
    font-size: 11px;
    line-height: var(--line-height-title);
  }

  .date {
    font-size: 12px;
  }
}
</style>
